import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

export interface ExportColumn {
  key: string;
  label: string;
  /** Optional value transform applied per cell. Receives raw value AND the full row. */
  formatter?: (value: any, row: any) => any;
}

export interface ExportContext {
  /** kebab-case report identifier — used in filename. */
  reportSlug: string;
  /** Pretty name — used in worksheet name and report header row. */
  reportName: string;
  /** School code/short name — used in filename. */
  schoolCode?: string;
  /** Academic year short label like "2025-26" — used in filename. */
  academicYear?: string;
  /** Optional filter summary lines printed at the top of the file. */
  filterSummary?: string[];
}

/** Standardized export pipeline shared by every report. */
@Injectable({ providedIn: 'root' })
export class ReportExportService {

  exportXlsx(
    columns: ExportColumn[],
    rows: any[],
    context: ExportContext
  ): void {
    const headerRows: any[][] = [];
    headerRows.push([context.reportName]);
    if (context.academicYear) headerRows.push([`Academic Year: ${context.academicYear}`]);
    if (context.schoolCode) headerRows.push([`School: ${context.schoolCode}`]);
    headerRows.push([`Generated: ${this.timestampLabel()}`]);
    if (context.filterSummary?.length) {
      for (const line of context.filterSummary) headerRows.push([line]);
    }
    headerRows.push([]);
    headerRows.push(columns.map(c => c.label));

    const dataRows = rows.map((row, idx) => columns.map(c => this.cell(row, c, idx)));

    const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows]);
    worksheet['!cols'] = columns.map(c => ({ wch: Math.max(c.label.length + 2, 14) }));

    const workbook: XLSX.WorkBook = {
      Sheets: { [this.sheetName(context.reportName)]: worksheet },
      SheetNames: [this.sheetName(context.reportName)]
    };
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    });
    saveAs(blob, this.fileName(context, 'xlsx'));
  }

  // CSV is intentionally data-only — no report-name / AY / school / generated / filter banner
  // rows. Spreadsheet apps treat those preamble lines as data; keep CSV importable by other tools.
  exportCsv(
    columns: ExportColumn[],
    rows: any[],
    context: ExportContext
  ): void {
    const lines: string[] = [];
    lines.push(columns.map(c => this.csvCell(c.label)).join(','));
    rows.forEach((row, idx) => {
      lines.push(columns.map(c => this.csvCell(this.cell(row, c, idx))).join(','));
    });
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, this.fileName(context, 'csv'));
  }

  /** Build a Blob without saving (for emailing). */
  buildXlsxBlob(columns: ExportColumn[], rows: any[], context: ExportContext): Blob {
    const headerRows: any[][] = [
      [context.reportName],
      [`Generated: ${this.timestampLabel()}`],
      [],
      columns.map(c => c.label)
    ];
    const dataRows = rows.map((row, idx) => columns.map(c => this.cell(row, c, idx)));
    const ws = XLSX.utils.aoa_to_sheet([...headerRows, ...dataRows]);
    ws['!cols'] = columns.map(c => ({ wch: Math.max(c.label.length + 2, 14) }));
    const wb: XLSX.WorkBook = { Sheets: { [this.sheetName(context.reportName)]: ws }, SheetNames: [this.sheetName(context.reportName)] };
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  fileName(context: ExportContext, ext: 'xlsx' | 'csv' | 'pdf'): string {
    const parts = [
      this.slugize(context.reportName) || context.reportSlug || 'Report',
      this.slugize(context.schoolCode),
      this.slugize(context.academicYear),
      this.timestampForFile()
    ].filter(p => !!p);
    return `${parts.join('_')}.${ext}`;
  }

  private cell(row: any, c: ExportColumn, rowIndex: number): any {
    // Sl.No column auto-generates a 1-based row number so reports don't have to
    // remember to inject one. Any per-column formatter still wins if provided.
    if (c.key === 'slNo' && !c.formatter) return rowIndex + 1;
    const raw = this.deepGet(row, c.key);
    return c.formatter ? c.formatter(raw, row) : (raw ?? '');
  }

  private deepGet(obj: any, key: string): any {
    if (!obj || !key) return undefined;
    if (key.indexOf('.') === -1) return obj[key];
    return key.split('.').reduce((acc: any, p) => (acc != null ? acc[p] : undefined), obj);
  }

  private csvCell(value: any): string {
    if (value === null || value === undefined) return '';
    const s = String(value);
    if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }

  private sheetName(reportName: string): string {
    // Excel sheet name limit is 31, no [ ] : * ? / \
    return (reportName || 'Report').replace(/[\[\]:\*\?\/\\]/g, ' ').slice(0, 31);
  }

  private slugize(s?: string): string {
    if (!s) return '';
    return s.replace(/[^A-Za-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  }

  private timestampForFile(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  }

  private timestampLabel(): string {
    return new Date().toLocaleString('en-IN');
  }
}
